import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Save, Trash2 } from "lucide-react";
import DeliveryDatePicker from "@/components/DeliveryDatePicker";
import { useOrderUnits } from "@/lib/useOrderUnits";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const isOrderDeadlinePassed = (schedule) => {
  const dl = schedule?.deadline;
  if (!dl) return false;
  return new Date() > new Date(dl.replace(" ", "T"));
};

const newRow = (columnNames = []) => ({
  id: Date.now() + Math.random(),
  product_name: "",
  quantity: "",
  unit: "g",
  delivery_date: "",
  usage_date: "",
  notes: "",
  breakdown: columnNames.length > 0 
    ? columnNames.reduce((acc, name) => ({ ...acc, [name]: "" }), {})
    : null,
});

export default function OtherOrdersPage() {
  const COMMON_UNITS = useOrderUnits();
  const [schedules, setSchedules] = useState([]);
  const [otherOrders, setOtherOrders] = useState([newRow()]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [multiColumnMode, setMultiColumnMode] = useState(false);
  const [columnNames, setColumnNames] = useState([]);

  useEffect(() => {
    fetchAll();
  }, []);

  // 多列モードが有効になったら、既存の行に内訳を追加
  useEffect(() => {
    if (multiColumnMode && columnNames.length > 0) {
      setOtherOrders(orders => 
        orders.map(order => ({
          ...order,
          breakdown: order.breakdown || columnNames.reduce((acc, name) => ({ ...acc, [name]: "" }), {})
        }))
      );
    }
  }, [multiColumnMode, columnNames]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      
      const requests = [
        axios.get(`${API}/delivery-schedules`, getAuthHeaders()),
        axios.get(`${API}/other-orders`, getAuthHeaders()),
      ];
      
      // 店舗ユーザーの場合、店舗情報も取得
      if (user && user.store_id) {
        requests.push(axios.get(`${API}/stores/${user.store_id}`, getAuthHeaders()));
      }
      
      const responses = await Promise.all(requests);
      setSchedules(responses[0].data);
      setHistory(responses[1].data);
      
      // 店舗情報がある場合
      if (responses[2]) {
        const store = responses[2].data;
        setStoreInfo(store);
        setMultiColumnMode(store.multi_column_mode || false);
        setColumnNames(store.column_names || []);
      }
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const addOrderRow = () => {
    setOtherOrders([...otherOrders, newRow(columnNames)]);
  };

  const removeOrderRow = (id) => {
    if (otherOrders.length === 1) {
      toast.error("最低1行は必要です");
      return;
    }
    setOtherOrders(otherOrders.filter((order) => order.id !== id));
  };

  const updateOrderRow = (id, field, value) => {
    setOtherOrders(
      otherOrders.map((order) =>
        order.id === id ? { ...order, [field]: value } : order
      )
    );
  };

  const handleSave = async () => {
    const validOrders = otherOrders.filter(
      (order) =>
        order.product_name &&
        order.quantity &&
        parseFloat(order.quantity) > 0 &&
        order.delivery_date
    );

    if (validOrders.length === 0) {
      toast.error("少なくとも1つの有効な発注を入力してください");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        orders: validOrders.map((order) => ({
          product_name: order.product_name,
          quantity: parseFloat(order.quantity),
          unit: order.unit || "g",
          delivery_date: order.delivery_date,
          usage_date: order.usage_date || order.delivery_date,
          notes: order.notes || null,
        })),
      };
      await axios.post(`${API}/other-orders/bulk`, payload, getAuthHeaders());
      toast.success(`${validOrders.length}件のリストなし発注を保存しました`);
      setOtherOrders([newRow()]);
      fetchAll();
    } catch (error) {
      const detail = error?.response?.data?.detail || "発注の保存に失敗しました";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  // 発注締切前の納品日のみ選択可
  const orderableSchedules = [...schedules]
    .filter((s) => !isOrderDeadlinePassed(s))
    .sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div className="flex items-stretch gap-3">
          <div className="w-1 bg-emerald-600 rounded-sm" />
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
              リストなし発注
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              商品リストにない商品を発注できます（発注締切前の納品日のみ）
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={addOrderRow}
            variant="outline"
            data-testid="add-row-button"
            className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md h-10"
          >
            <Plus className="h-4 w-4 mr-2" />
            行を追加
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-other-orders-button"
            className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-md h-10 px-5"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "保存中..." : "発注を保存"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <div className="space-y-4">
              {otherOrders.map((order, index) => (
                <div
                  key={order.id}
                  className={`grid ${multiColumnMode ? 'grid-cols-14' : 'grid-cols-12'} gap-4 items-end p-4 border border-slate-200 rounded-md bg-slate-50/50`}
                >
                  <div className="col-span-3">
                    <Label htmlFor={`product-name-${order.id}`} className="text-sm font-medium">
                      商品名
                    </Label>
                    <Input
                      id={`product-name-${order.id}`}
                      data-testid={`product-name-${index}`}
                      value={order.product_name}
                      onChange={(e) => updateOrderRow(order.id, "product_name", e.target.value)}
                      placeholder="例: 特別な野菜"
                      lang="ja"
                      inputMode="text"
                      autoCapitalize="off"
                      style={{ imeMode: "active" }}
                      className="mt-1 h-9 rounded-md"
                    />
                  </div>

                  {multiColumnMode ? (
                    <>
                      <div className="col-span-1">
                        <Label htmlFor={`patients-${order.id}`} className="text-sm font-medium text-blue-700">
                          患者
                        </Label>
                        <Input
                          id={`patients-${order.id}`}
                          data-testid={`patients-${index}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={order.breakdown?.patients || ""}
                          onChange={(e) => {
                            const patients = parseFloat(e.target.value) || 0;
                            const staff = parseFloat(order.breakdown?.staff) || 0;
                            const total = patients + staff;
                            setOtherOrders(
                              otherOrders.map((o) =>
                                o.id === order.id
                                  ? {
                                      ...o,
                                      breakdown: {
                                        ...o.breakdown,
                                        patients: e.target.value,
                                        staff: o.breakdown?.staff || "",
                                        total: total.toFixed(2)
                                      },
                                      quantity: total.toFixed(2)
                                    }
                                  : o
                              )
                            );
                          }}
                          placeholder="0.00"
                          className="mt-1 h-9 rounded-md bg-blue-50"
                        />
                      </div>
                      <div className="col-span-1">
                        <Label htmlFor={`staff-${order.id}`} className="text-sm font-medium text-green-700">
                          職員
                        </Label>
                        <Input
                          id={`staff-${order.id}`}
                          data-testid={`staff-${index}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={order.breakdown?.staff || ""}
                          onChange={(e) => {
                            const staff = parseFloat(e.target.value) || 0;
                            const patients = parseFloat(order.breakdown?.patients) || 0;
                            const total = patients + staff;
                            setOtherOrders(
                              otherOrders.map((o) =>
                                o.id === order.id
                                  ? {
                                      ...o,
                                      breakdown: {
                                        ...o.breakdown,
                                        patients: o.breakdown?.patients || "",
                                        staff: e.target.value,
                                        total: total.toFixed(2)
                                      },
                                      quantity: total.toFixed(2)
                                    }
                                  : o
                              )
                            );
                          }}
                          placeholder="0.00"
                          className="mt-1 h-9 rounded-md bg-green-50"
                        />
                      </div>
                      <div className="col-span-1">
                        <Label htmlFor={`total-${order.id}`} className="text-sm font-medium text-slate-700">
                          合計
                        </Label>
                        <Input
                          id={`total-${order.id}`}
                          data-testid={`total-${index}`}
                          type="text"
                          value={order.breakdown?.total || order.quantity || "0.00"}
                          readOnly
                          className="mt-1 h-9 rounded-md bg-slate-100 text-slate-700 font-semibold"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="col-span-1">
                      <Label htmlFor={`quantity-${order.id}`} className="text-sm font-medium">
                        数量
                      </Label>
                      <Input
                        id={`quantity-${order.id}`}
                        data-testid={`quantity-${index}`}
                        type="number"
                        step="0.01"
                        min="0"
                        value={order.quantity}
                        onChange={(e) => updateOrderRow(order.id, "quantity", e.target.value)}
                        placeholder="0.00"
                        className="mt-1 h-9 rounded-md"
                      />
                    </div>
                  )}

                  <div className="col-span-1">
                    <Label htmlFor={`unit-${order.id}`} className="text-sm font-medium">
                      単位
                    </Label>
                    <Select
                      value={order.unit}
                      onValueChange={(value) => updateOrderRow(order.id, "unit", value)}
                    >
                      <SelectTrigger
                        data-testid={`unit-${index}`}
                        className="mt-1 h-9 rounded-md"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COMMON_UNITS.map((unit) => (
                          <SelectItem key={unit} value={unit}>
                            {unit}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-2">
                    <Label className="text-sm font-medium">納品日</Label>
                    <div className="mt-1">
                      <DeliveryDatePicker
                        value={order.delivery_date}
                        onChange={(d) => {
                          updateOrderRow(order.id, "delivery_date", d);
                          // 納品日が変更されたら使用日もデフォルト設定
                          if (!order.usage_date) {
                            updateOrderRow(order.id, "usage_date", d);
                          }
                        }}
                        schedules={orderableSchedules}
                        testId={`delivery-${index}`}
                        placeholder="配送日を選択"
                      />
                    </div>
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor={`usage-date-${order.id}`} className="text-sm font-medium">
                      使用日
                    </Label>
                    <Input
                      id={`usage-date-${order.id}`}
                      data-testid={`usage-date-${index}`}
                      type="date"
                      value={order.usage_date || order.delivery_date}
                      onChange={(e) => updateOrderRow(order.id, "usage_date", e.target.value)}
                      className="mt-1 h-9 rounded-md bg-yellow-50"
                    />
                  </div>

                  <div className="col-span-2">
                    <Label htmlFor={`notes-${order.id}`} className="text-sm font-medium">
                      備考
                    </Label>
                    <Input
                      id={`notes-${order.id}`}
                      data-testid={`notes-${index}`}
                      value={order.notes}
                      onChange={(e) => updateOrderRow(order.id, "notes", e.target.value)}
                      placeholder="任意"
                      lang="ja"
                      inputMode="text"
                      autoCapitalize="off"
                      style={{ imeMode: "active" }}
                      className="mt-1 h-9 rounded-md"
                    />
                  </div>

                  <div className="col-span-1">
                    <Button
                      onClick={() => removeOrderRow(order.id)}
                      variant="ghost"
                      size="sm"
                      data-testid={`delete-row-${index}`}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                <strong>ご注意:</strong> 発注締切前の納品日のみ選択できます。締切後の変更は「発注確認＆変更」から申請してください。
              </p>
            </div>
          </div>

          {/* 発注履歴 */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-800">
                リストなし発注 履歴
              </h3>
              <span className="text-xs text-slate-500">{history.length} 件</span>
            </div>
            <div className="px-4 pt-3">
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                発注内容の変更・キャンセルは「発注確認＆変更」ページから行ってください。
              </div>
            </div>
            {history.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                履歴はありません
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs">
                    <tr>
                      <th className="px-4 py-2 text-left">納品日</th>
                      <th className="px-4 py-2 text-left">商品名</th>
                      <th className="px-4 py-2 text-right">数量</th>
                      <th className="px-4 py-2 text-left">単位</th>
                      <th className="px-4 py-2 text-left">備考</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-2 font-mono text-xs">{h.delivery_date}</td>
                        <td className="px-4 py-2 text-slate-800 font-medium">
                          {h.product_name}
                          {h.linked_product_name && (
                            <div className="text-xs text-emerald-700 font-normal mt-0.5" data-testid={`history-linked-${h.id}`}>
                              → {h.linked_product_name} として登録
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">{Number(h.quantity).toFixed(2)}</td>
                        <td className="px-4 py-2 text-slate-600">{h.unit}</td>
                        <td className="px-4 py-2 text-slate-500 text-xs">{h.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
